import type {
  CodeItemAttributeSchemaScope,
  CodeItemGroup,
  CodeRow,
  RuleNode,
} from '../types/codeIndex'
import { normalizeAttributeSchemaScopes, normalizeAttributeValues } from './codeItemAttributes'
import { normalizeAppendixRefs, normalizeGroupAppendixRefs } from './appendixRefs'
import { deriveCompositeSubgroups } from './compositeCodeUtils'
import type { BranchTree, BranchTreeNode } from '../services/projectService'
import { resolveCodeItemLen } from '../services/codeIndexService'

export interface BranchNodeUpdate {
  elementId: string
  properties: Record<string, string>
}

export interface BranchTreeUpdateRequest {
  changeId?: string
  updates: BranchNodeUpdate[]
}

const FORMAT_CODE_REL = 'format_code'
const FORMAT_VALUE_REL = 'format_value'
const HAS_RULE_REL = 'HAS_RULE'

function getProperty(
  properties: Record<string, string | null | undefined> | undefined,
  ...keys: string[]
): string | null {
  if (!properties) return null

  for (const key of keys) {
    const value = properties[key]
    if (value !== undefined && value !== null) return String(value)
  }

  return null
}

function toNumber(value: string | null, fallback: number): number {
  if (value === null || value.trim() === '') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function parseJsonValue<T>(value: string | null, fallback: T): T {
  if (!value) return fallback

  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

function stringifyJsonValue(value: unknown): string {
  return JSON.stringify(value ?? null)
}

function normalizeStatusValue(value: string | null | undefined): string {
  const normalizedValue = String(value ?? '').trim().toUpperCase()
  return normalizedValue === 'EMPTY' ? '' : normalizedValue
}

function getNodeElementId(groupOrRow: { id?: string | null }): string | null {
  const normalizedId = String(groupOrRow.id || '').trim()
  return normalizedId || null
}

function buildChildrenByParent(tree: BranchTree): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>()

  tree.edges.forEach((edge) => {
    if (!childrenByParent.has(edge.parentElementId)) {
      childrenByParent.set(edge.parentElementId, [])
    }
    childrenByParent.get(edge.parentElementId)?.push(edge.childElementId)
  })

  return childrenByParent
}

function buildNodesById(tree: BranchTree): Map<string, BranchTreeNode> {
  return new Map(tree.nodes.map((node) => [node.elementId, node]))
}

function getChildrenByRelationship(tree: BranchTree, parentElementId: string, relType: string): BranchTreeNode[] {
  const nodesById = buildNodesById(tree)
  return tree.edges
    .filter((edge) => edge.parentElementId === parentElementId && edge.relType === relType)
    .map((edge) => nodesById.get(edge.childElementId))
    .filter((node): node is BranchTreeNode => Boolean(node))
}

function getRootFormatCodeNodes(tree: BranchTree): BranchTreeNode[] {
  const nodesById = buildNodesById(tree)
  const rootIds = tree.rootNodeIds.length
    ? tree.rootNodeIds
    : tree.edges
      .filter((edge) => edge.relType === FORMAT_CODE_REL)
      .map((edge) => edge.childElementId)

  return rootIds
    .map((rootId) => nodesById.get(rootId))
    .filter((node): node is BranchTreeNode => Boolean(node && node.nodeType === 'FormatCode'))
}

function mapRuleNode(node: BranchTreeNode | null | undefined): RuleNode | null {
  if (!node) return null

  const properties = node.properties || {}
  const id = node.elementId
  const description = getProperty(properties, 'description')

  if (node.nodeType === 'LoopNode') {
    return {
      id,
      type: 'while',
      reference: getProperty(properties, 'reference') || '',
      condition: getProperty(properties, 'condition') || '',
      value: getProperty(properties, 'value') || '',
      description,
      freeText: null,
      appendix: null,
    }
  }

  if (node.nodeType === 'IfNode') {
    return {
      id,
      type: 'if',
      reference: getProperty(properties, 'reference'),
      condition: getProperty(properties, 'condition'),
      value: getProperty(properties, 'value'),
      returnValue: getProperty(properties, 'return_value', 'returnValue'),
      conditionGroup: parseJsonValue(getProperty(properties, 'condition_group', 'conditionGroup'), null),
      consequenceGroups: parseJsonValue(getProperty(properties, 'consequence_groups', 'consequenceGroups'), null),
      future: getProperty(properties, 'is_future', 'future') === 'True' || getProperty(properties, 'is_future', 'future') === 'true',
      if: null,
      else: null,
      description,
      freeText: null,
      appendix: null,
    }
  }

  if (description) {
    return {
      id,
      type: 'text',
      description,
      freeText: description,
      appendix: null,
    }
  }

  return null
}

function mapFormatValueNode(tree: BranchTree, node: BranchTreeNode, rowIndex: number): CodeRow {
  const properties = node.properties || {}
  const rule = mapRuleNode(getChildrenByRelationship(tree, node.elementId, HAS_RULE_REL)[0])
  const optionCode = getProperty(properties, 'value', 'name') || ''
  const rowId = node.elementId

  return {
    id: node.elementId,
    rowId,
    optionCode,
    status: normalizeStatusValue(getProperty(properties, 'status')),
    group: getProperty(properties, 'group')?.trim() || null,
    valuePosition: (() => {
      const value = getProperty(properties, 'value_position', 'valuePosition')
      return value === null || value.trim() === '' ? null : toNumber(value, 0)
    })(),
    valuePositionEnd: (() => {
      const value = getProperty(properties, 'value_position_end', 'valuePositionEnd')
      return value === null || value.trim() === '' ? null : toNumber(value, 0)
    })(),
    position: toNumber(getProperty(properties, 'position'), rowIndex),
    description: getProperty(properties, 'desc', 'description'),
    descriptionBlockId: getProperty(properties, 'description_block_id', 'descriptionBlockId') || rowId,
    availabilityBlockId: getProperty(properties, 'availability_block_id', 'availabilityBlockId') || rowId,
    regions: (getProperty(properties, 'regions') || '')
      .split(',')
      .map((region) => region.trim())
      .filter(Boolean),
    rule,
    appendix: null,
    appendixRefs: parseAppendixRefsProperty(getProperty(properties, 'appendix_refs', 'appendixRefs')),
    attributeValues: normalizeAttributeValues(
      parseJsonValue<Record<string, string | null>>(getProperty(properties, 'attribute_values', 'attributeValues'), {})
    ),
  }
}

// Branch tree properties are stringified: a list arrives as "id1,id2".
function parseAppendixRefsProperty(value: string | null): string[] {
  return normalizeAppendixRefs((value || '').split(',').map(ref => ref.trim()).filter(Boolean))
}

function formatAppendixRefsProperty(value: unknown): string {
  return Array.isArray(value) ? normalizeAppendixRefs(value).join(',') : ''
}

function mapFormatCodeNode(tree: BranchTree, node: BranchTreeNode, groupIndex: number): CodeItemGroup {
  const properties = node.properties || {}
  const start = toNumber(getProperty(properties, 'position'), groupIndex + 1)
  // len is the inclusive span (end - start): 0 is a single position, 1 two positions.
  const len = Math.max(0, toNumber(getProperty(properties, 'len'), 0))
  const code = getProperty(properties, 'name') || getProperty(properties, 'code_item', 'codeItem') || `C${String(groupIndex + 1).padStart(2, '0')}`
  const end = start + len
  const rows = getChildrenByRelationship(tree, node.elementId, FORMAT_VALUE_REL)
    .sort((a, b) => toNumber(getProperty(a.properties, 'position'), 0) - toNumber(getProperty(b.properties, 'position'), 0))
    .map((rowNode, rowIndex) => mapFormatValueNode(tree, rowNode, rowIndex))
  const attributeSchemas = normalizeAttributeSchemaScopes(
    parseJsonValue<CodeItemAttributeSchemaScope[]>(getProperty(properties, 'attribute_schemas', 'attributeSchemas'), [])
  )
  const composite = rows.some(row => Number.isInteger(row.valuePosition))

  return {
    id: node.elementId,
    localKey: `project:${node.elementId}`,
    codeItemPos: {
      code,
      posRange: [start, end],
      label: `${code} [${start}-${end}]`,
      isDefault: false,
    },
    title: getProperty(properties, 'title', 'desc')?.trim() || null,
    attributeSchemas,
    authoringMode: composite ? 'composite' : 'simple',
    subgroups: composite ? deriveCompositeSubgroups(rows) : [],
    appendixRefs: parseAppendixRefsProperty(getProperty(properties, 'appendix_refs', 'appendixRefs')),
    groupAppendixRefs: normalizeGroupAppendixRefs(
      parseJsonValue<Record<string, string[]>>(getProperty(properties, 'group_appendix_refs', 'groupAppendixRefs'), {})
    ),
    rows,
  }
}

export function mapBranchTreeToCodeItemGroups(tree: BranchTree): CodeItemGroup[] {
  const childrenByParent = buildChildrenByParent(tree)

  return getRootFormatCodeNodes(tree)
    .filter((node) => childrenByParent.has(node.elementId) || node.nodeType === 'FormatCode')
    .sort((a, b) => toNumber(getProperty(a.properties, 'position'), 0) - toNumber(getProperty(b.properties, 'position'), 0))
    .map((node, groupIndex) => mapFormatCodeNode(tree, node, groupIndex))
}

function valuesEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

function addChangedProperty(
  props: Record<string, string>,
  key: string,
  before: unknown,
  after: unknown,
  formatter: (value: unknown) => string = (value) => String(value ?? '')
) {
  if (!valuesEqual(before, after)) {
    props[key] = formatter(after)
  }
}

function buildGroupUpdate(original: CodeItemGroup, current: CodeItemGroup): BranchNodeUpdate | null {
  const elementId = getNodeElementId(current)
  if (!elementId) return null

  const props: Record<string, string> = {}
  const currentLen = resolveCodeItemLen(current)
  const originalLen = resolveCodeItemLen(original)

  addChangedProperty(props, 'name', original.codeItemPos.code, current.codeItemPos.code)
  addChangedProperty(props, 'position', original.codeItemPos.posRange[0], current.codeItemPos.posRange[0])
  addChangedProperty(props, 'len', originalLen, currentLen)
  addChangedProperty(props, 'title', original.title ?? '', current.title ?? '')
  addChangedProperty(
    props,
    'attribute_schemas',
    normalizeAttributeSchemaScopes(original.attributeSchemas),
    normalizeAttributeSchemaScopes(current.attributeSchemas),
    stringifyJsonValue
  )
  addChangedProperty(
    props,
    'appendix_refs',
    normalizeAppendixRefs(original.appendixRefs),
    normalizeAppendixRefs(current.appendixRefs),
    formatAppendixRefsProperty
  )
  addChangedProperty(
    props,
    'group_appendix_refs',
    normalizeGroupAppendixRefs(original.groupAppendixRefs),
    normalizeGroupAppendixRefs(current.groupAppendixRefs),
    stringifyJsonValue
  )

  return Object.keys(props).length ? { elementId, properties: props } : null
}

function buildRowUpdate(original: CodeRow, current: CodeRow): BranchNodeUpdate | null {
  const elementId = getNodeElementId(current)
  if (!elementId || current.isNew) return null

  const props: Record<string, string> = {}

  addChangedProperty(props, 'name', original.optionCode, current.optionCode)
  addChangedProperty(props, 'value', original.optionCode, current.optionCode)
  addChangedProperty(props, 'desc', original.description ?? '', current.description ?? '')
  addChangedProperty(props, 'status', normalizeStatusValue(original.status), normalizeStatusValue(current.status))
  addChangedProperty(props, 'group', original.group ?? '', current.group ?? '')
  addChangedProperty(props, 'position', original.position, current.position)
  addChangedProperty(props, 'value_position', original.valuePosition ?? '', current.valuePosition ?? '')
  addChangedProperty(props, 'value_position_end', original.valuePositionEnd ?? '', current.valuePositionEnd ?? '')
  addChangedProperty(props, 'description_block_id', original.descriptionBlockId, current.descriptionBlockId)
  addChangedProperty(props, 'availability_block_id', original.availabilityBlockId, current.availabilityBlockId)
  addChangedProperty(props, 'regions', original.regions ?? [], current.regions ?? [], (value) => Array.isArray(value) ? value.join(',') : '')
  addChangedProperty(
    props,
    'appendix_refs',
    normalizeAppendixRefs(original.appendixRefs),
    normalizeAppendixRefs(current.appendixRefs),
    formatAppendixRefsProperty
  )
  addChangedProperty(
    props,
    'attribute_values',
    normalizeAttributeValues(original.attributeValues),
    normalizeAttributeValues(current.attributeValues),
    stringifyJsonValue
  )

  return Object.keys(props).length ? { elementId, properties: props } : null
}

function buildRuleUpdate(original: RuleNode | null | undefined, current: RuleNode | null | undefined): BranchNodeUpdate | null {
  const elementId = String(current?.id || '').trim()
  if (!elementId || !original || !current || original.type !== current.type) return null

  const props: Record<string, string> = {}
  addChangedProperty(props, 'description', original.description ?? '', current.description ?? '')

  if (current.type === 'while' && original.type === 'while') {
    addChangedProperty(props, 'reference', original.reference, current.reference)
    addChangedProperty(props, 'condition', original.condition, current.condition)
    addChangedProperty(props, 'value', original.value, current.value)
  }

  if (current.type === 'if' && original.type === 'if') {
    addChangedProperty(props, 'reference', original.reference ?? '', current.reference ?? '')
    addChangedProperty(props, 'condition', original.condition ?? '', current.condition ?? '')
    addChangedProperty(props, 'value', original.value ?? '', current.value ?? '')
    addChangedProperty(props, 'return_value', original.returnValue ?? '', current.returnValue ?? '')
    addChangedProperty(props, 'condition_group', original.conditionGroup ?? null, current.conditionGroup ?? null, stringifyJsonValue)
    addChangedProperty(props, 'consequence_groups', original.consequenceGroups ?? null, current.consequenceGroups ?? null, stringifyJsonValue)
    addChangedProperty(props, 'is_future', Boolean(original.future), Boolean(current.future), (value) => String(Boolean(value)))
  }

  return Object.keys(props).length ? { elementId, properties: props } : null
}

export function buildBranchTreeUpdateRequest(
  originalGroups: CodeItemGroup[],
  currentGroups: CodeItemGroup[]
): BranchTreeUpdateRequest {
  const updates: BranchNodeUpdate[] = []
  const originalGroupsById = new Map(originalGroups.map((group) => [getNodeElementId(group), group]))

  currentGroups.forEach((currentGroup) => {
    const groupId = getNodeElementId(currentGroup)
    const originalGroup = groupId ? originalGroupsById.get(groupId) : null
    if (!originalGroup) return

    const groupUpdate = buildGroupUpdate(originalGroup, currentGroup)
    if (groupUpdate) updates.push(groupUpdate)

    const originalRowsById = new Map(originalGroup.rows.map((row) => [getNodeElementId(row), row]))
    currentGroup.rows.forEach((currentRow) => {
      const rowId = getNodeElementId(currentRow)
      const originalRow = rowId ? originalRowsById.get(rowId) : null
      if (!originalRow) return

      const rowUpdate = buildRowUpdate(originalRow, currentRow)
      if (rowUpdate) updates.push(rowUpdate)

      const ruleUpdate = buildRuleUpdate(originalRow.rule, currentRow.rule)
      if (ruleUpdate) updates.push(ruleUpdate)
    })
  })

  return { updates }
}
